import React, { useContext } from 'react';
import classNames from 'classnames';
import EDListItem, {
  EDListItemButton,
} from '../../../../../../../../../../BaseComponents/List/Components/EDListItem';
import EkonomiskAnalysSideMenuIconButton from '../../../Components/IconButton/EkonomiskAnalysSideMenuIconButton';
import EkonomiskAnalysSelectSearch from '../EkonomiskAnalysSelectSearch/EkonomiskAnalysSelectSearch';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { ISelectedAnalys } from '../../../../../EkonomiskAnalysPage';
import { EkonomiskAnalysSideMenuContext } from '../../../Context/EkonomiskAnalysSideMenuContext';
import { useIsTouchDevice } from '../../../../../../../../../../Components/BreakpointContext/BreakpointContextUtils';

interface IProps {
  selectListOpen: boolean;
  toggleSelectListOpen: () => void;
  onSearchValueChanged: (newValue: string) => void;
  onSearchInputClicked: () => void;
  searchValue: string;
  selectedAnalys?: ISelectedAnalys | null;
  sideMenuIsOpen: boolean;
  displayValue: string | null;
  setDisplayValue: (newValue: string) => void;
  openFullscreen: (callback?: () => void) => void;
  fullscreen?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  classes?: any;
}

const styles = (
  theme: Theme,
  _params: any,
  classes: Record<'focused', string>
) => ({
  listItem: {
    paddingRight: theme.spacing(1.5),
    marginBottom: 1,
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: 8,
    outline: 0,
  },
  focused: {},
  listItemSideMenuOpen: {
    paddingTop: 11,
    paddingBottom: 11,
    paddingLeft: 24,
    marginBottom: 0,
    marginTop: 0,
    backgroundColor:
      theme.customProperties.EASideMenuColors.selectedOptionsSectionBGColor,
  },
  listItemFullscreen: {
    backgroundColor: 'inherit',
    padding: 0,
    '&:hover': {
      backgroundColor: 'inherit',
    },
    [`&.${classes.focused}`]: {
      backgroundColor: 'inherit',
    },
  },
  iconButton: {
    margin: theme.spacing(2, 0, 0, -1), // top 24 för att TextEdit label tar upp 24 px
  },
  iconSideMenuOpen: {
    margin: theme.spacing(4.5, 0, 1.5),
  },
  label: {
    marginLeft: theme.spacing(-4.75), // Flytta label till vänster för att vara alignad med ikonen
  },
  labelFullscreen: {
    marginLeft: 0,
    color: 'inherit',
    [`&.${classes.focused}`]: {
      color: 'inherit',
    },
  },
  underlineFullscreen: {
    '&:before': {
      borderColor: theme.palette.common.white,
    },
    '&:hover:not([disabled]):before': {
      borderBottom: `1px solid ${theme.palette.common.white}`,
    },
    '&:after': {
      borderColor: theme.palette.common.white,
    },
  },
  textEdit: {
    marginLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
});

const onListItemClick =
  (toggleSelectListOpen: () => void, next: () => void, isTouchDevice: boolean) =>
  () => {
    toggleSelectListOpen();
    if (!isTouchDevice) {
      next();
    }
  };

const EkonomiskAnalysSelectSearchTab: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { userInfo } = useContext(EkonomiskAnalysSideMenuContext);
  const isTouchDevice = useIsTouchDevice();
  const analysKodbehorighet = props.selectedAnalys
    ? props.selectedAnalys.codePrivilege.name
    : undefined;
  const analysNamn = props.selectedAnalys
    ? props.selectedAnalys.analys.name
    : undefined;

  return props.sideMenuIsOpen ? (
    <EDListItemButton
      onClick={
        props.sideMenuIsOpen
          ? onListItemClick(
              props.toggleSelectListOpen,
              props.onSearchInputClicked,
              isTouchDevice
            )
          : undefined
      }
      className={classNames(props.classes.listItem, {
        [props.classes.listItemSideMenuOpen]: props.sideMenuIsOpen,
        [props.classes.listItemFullscreen]: props.fullscreen,
      })}
      classes={{
        focusVisible: props.classes.focused,
      }}
      testId="EkonomiskAnalysSelectSearchListItem"
    >
      {!props.fullscreen && (
        <EkonomiskAnalysSideMenuIconButton
          sideMenuOpen={props.sideMenuIsOpen}
          tooltipText={'Välj analys'}
          iconType={'ekonomisk_analys'}
          onClick={onListItemClick(
            props.toggleSelectListOpen,
            props.onSearchInputClicked,
            isTouchDevice
          )}
          classes={{
            iconButton: props.classes.iconButton,
          }}
          disabled={props.disabled}
          disabledReason={props.disabledReason}
          color={props.disabled ? 'disabled' : undefined}
          marginTop={props.sideMenuIsOpen && 3}
        />
      )}
      <EkonomiskAnalysSelectSearch
        onSearchInputClicked={props.onSearchInputClicked}
        openFullscreen={props.openFullscreen}
        analysNamn={analysNamn}
        searchValue={props.searchValue}
        onSearchValueChanged={props.onSearchValueChanged}
        selectListOpen={props.selectListOpen}
        hideLabel={!props.sideMenuIsOpen}
        displayValue={props.displayValue}
        setDisplayValue={props.setDisplayValue}
        classes={{
          label: classNames(props.classes.label, {
            [props.classes.labelFullscreen]: props.fullscreen,
          }),
          underline: classNames({
            [props.classes.underlineFullscreen]: props.fullscreen,
          }),
          labelFocused: classNames({
            [props.classes.focused]: props.fullscreen,
          }),
        }}
        className={!props.fullscreen ? props.classes.textEdit : undefined}
        label={
          analysKodbehorighet && !props.selectListOpen
            ? `Vald analys inom ${analysKodbehorighet}`
            : userInfo &&
              userInfo.analysGroupingMode &&
              userInfo.analysGroupingMode.value === 'kodbeh'
            ? 'Välj kodbehörighet och analys'
            : ' Välj analys och kodbehörighet'
        }
        fullscreen={props.fullscreen}
        captureInputRef
      />
    </EDListItemButton>
  ) : (
    <EDListItem
      className={classNames(props.classes.listItem, {
        [props.classes.listItemSideMenuOpen]: props.sideMenuIsOpen,
        [props.classes.listItemFullscreen]: props.fullscreen,
      })}
      classes={{
        focusVisible: props.classes.focused,
      }}
      testId="EkonomiskAnalysSelectSearchListItem"
    >
      {!props.fullscreen && (
        <EkonomiskAnalysSideMenuIconButton
          sideMenuOpen={props.sideMenuIsOpen}
          tooltipText={'Välj analys'}
          iconType={'ekonomisk_analys'}
          onClick={onListItemClick(
            props.toggleSelectListOpen,
            props.onSearchInputClicked,
            isTouchDevice
          )}
          classes={{
            iconButton: props.classes.iconButton,
          }}
          disabled={props.disabled}
          disabledReason={props.disabledReason}
          color={props.disabled ? 'disabled' : undefined}
          marginTop={props.sideMenuIsOpen && 3}
        />
      )}
      <EkonomiskAnalysSelectSearch
        onSearchInputClicked={props.onSearchInputClicked}
        openFullscreen={props.openFullscreen}
        analysNamn={analysNamn}
        searchValue={props.searchValue}
        onSearchValueChanged={props.onSearchValueChanged}
        selectListOpen={props.selectListOpen}
        hideLabel={!props.sideMenuIsOpen}
        displayValue={props.displayValue}
        setDisplayValue={props.setDisplayValue}
        classes={{
          label: classNames(props.classes.label, {
            [props.classes.labelFullscreen]: props.fullscreen,
          }),
          underline: classNames({
            [props.classes.underlineFullscreen]: props.fullscreen,
          }),
          labelFocused: classNames({
            [props.classes.focused]: props.fullscreen,
          }),
        }}
        className={!props.fullscreen ? props.classes.textEdit : undefined}
        label={
          analysKodbehorighet && !props.selectListOpen
            ? `Vald analys inom ${analysKodbehorighet}`
            : userInfo &&
              userInfo.analysGroupingMode &&
              userInfo.analysGroupingMode.value === 'kodbeh'
            ? 'Välj kodbehörighet och analys'
            : ' Välj analys och kodbehörighet'
        }
        fullscreen={props.fullscreen}
        captureInputRef
      />
    </EDListItem>
  );
};

export default withStyles(EkonomiskAnalysSelectSearchTab, styles, {
  uniqId: '0c794134-c9a0-433f-91a3-80e6a790ac1b',
});
